
import { supabase } from '@/integrations/supabase/client';
import { generateBroCode } from './broCodeGenerator';
import { BroData } from './broCodeTypes';
import { validateAddress } from '@/lib/addressValidation';

export interface BroCodeResult {
  brocode: string;
  error?: string;
}

export interface RerollBroCodeResult {
  brocode: string;
  error?: string;
}

// Path mapping function for rank to chosenPath conversion
function pathFromRank(rank: string): 'social' | 'shadow' | 'liquidity' | null {
  switch (rank.toLowerCase()) {
    case 'social bro': return 'social';
    case 'shadow bro': return 'shadow';
    case 'initiated bro': return 'liquidity'; // Default path for initiated bros
    case 'liquidity bro': return 'liquidity';
    default: return null; // Will use default path in edge function
  }
}

// 🚨 LEGACY FUNCTION - DEPRECATED FOR NEW ONBOARDING
// Only used for rerolls and manual BroCode generation
export const generateBroCodeWithPrefix = (prefix: string): string => {
  console.warn('⚠️ LEGACY: generateBroCodeWithPrefix used - consider using complete-initiation edge function');
  
  if (prefix === 'BROMETHEUS') {
    console.warn('⚠️ BROMETHEUS prefix requested - this should only be through upgrade path');
  }
  
  const code = generateBroCode();
  const fullBroCode = `${prefix}-${code}`;
  
  console.log(`✅ Generated legacy BroCode: ${fullBroCode}`);
  return fullBroCode;
};

// Enhanced logging function with address validation
const logBroCodeGeneration = async (
  userId: string, 
  oldBroCode: string | null, 
  newBroCode: string, 
  generationPath: string
): Promise<void> => {
  try {
    await supabase
      .from('brocode_generation_log')
      .insert({
        user_id: userId,
        old_brocode: oldBroCode,
        new_brocode: newBroCode,
        generation_path: generationPath
      });
    console.log(`📝 Logged BroCode generation: ${generationPath} -> ${newBroCode}`);
  } catch (error) {
    console.error('❌ Failed to log BroCode generation:', error);
  }
};

// Check if a wallet has already been initiated (supports both BSC and Solana)
export const checkWalletInitialized = async (walletAddress: string): Promise<boolean> => {
  try {
    console.log(`Checking if wallet ${walletAddress} is already initiated...`);
    
    // Validate address
    const validation = validateAddress(walletAddress);
    if (!validation.isValid) {
      console.error('Invalid wallet address format:', walletAddress);
      return false;
    }
    
    const { data, error } = await supabase
      .from('initiated_bros')
      .select('*')
      .eq('wallet_address', validation.normalizedAddress)
      .single();
      
    if (error) {
      console.log('No existing BroCode found for this wallet');
      return false;
    }
    
    console.log('Existing BroCode found:', data);
    return true;
  } catch (error) {
    console.error('Error checking wallet initialization:', error);
    return false;
  }
};

// Get existing BroCode data for a wallet (supports both BSC and Solana)
export const getExistingBroCode = async (walletAddress: string): Promise<BroData | null> => {
  try {
    // Validate address
    const validation = validateAddress(walletAddress);
    if (!validation.isValid) {
      console.error('Invalid wallet address format:', walletAddress);
      return null;
    }
    
    const { data, error } = await supabase
      .from('initiated_bros')
      .select('*')
      .eq('wallet_address', validation.normalizedAddress)
      .single();
      
    if (error) {
      return null;
    }
    
    return data as BroData;
  } catch (error) {
    console.error('Error getting existing BroCode:', error);
    return null;
  }
};

// 🚨 LEGACY FUNCTION - DEPRECATED
// Save initial BroCode (legacy function for backward compatibility)
export const saveInitialBroCode = async (
  walletAddress: string,
  rank: string,
  _broScore: number = 0
): Promise<string | null> => {
  console.warn('⚠️ LEGACY: saveInitialBroCode used - consider using complete-initiation edge function');
  try {
    const result = await insertBroWithRank(walletAddress, rank, 'initial_generation');
    return result.error ? null : result.brocode;
  } catch (error) {
    console.error('Error in saveInitialBroCode:', error);
    return null;
  }
};

// 🔄 REFACTORED: Now calls complete-initiation edge function instead of legacy logic
export const insertBroWithRank = async (
  walletAddress: string,
  rank: string,
  generationPath: string = 'unknown'
): Promise<{ brocode: string; error?: string }> => {
  try {
    console.log(`🚀 Creating new bro with rank: ${rank}, path: ${generationPath}`);
    
    // Validate address
    const validation = validateAddress(walletAddress);
    if (!validation.isValid) {
      console.error(`❌ Invalid wallet address: ${walletAddress}`);
      return { brocode: '', error: 'Invalid wallet address format' };
    }

    // Map rank to chosenPath for the edge function
    const chosenPath = pathFromRank(rank);
    console.log(`🏷️ Mapped rank "${rank}" to path: ${chosenPath}`);

    // For legacy web calls, we need to add a note that this might not have auth headers
    console.warn('⚠️ Legacy insertBroWithRank call - authentication may not be available');
    
    // Call the complete-initiation edge function
    const { data, error } = await supabase.functions.invoke('complete-initiation', {
      body: {
        walletAddress: validation.normalizedAddress,
        telegramId: null, // Not available in web context
        chainType: validation.type === 'solana' ? 'sol' : 'eth',
        chosenPath: chosenPath,
        twitterHandle: null // Not available in this context
      }
    });

    if (error) {
      console.error('❌ Error calling complete-initiation:', error);
      return { brocode: '', error: error.message || 'Failed to create BroCode' };
    }

    if (!data.success) {
      console.error('❌ Edge function returned error:', data.error);
      return { brocode: '', error: data.error || 'Failed to create BroCode' };
    }

    const { brocode } = data.userData;
    console.log(`✅ Successfully created bro via edge function: ${brocode} with rank: ${rank} for ${validation.type} address`);
    
    return { brocode };
    
  } catch (error) {
    console.error('❌ Unexpected error in insertBroWithRank:', error);
    return { brocode: '', error: 'Failed to create BroCode' };
  }
};

// ✅ PRESERVED: Still needed for reroll functionality
export const rerollBroCode = async (
  walletAddress: string,
  currentBroScore: number,
  rerollCost: number = 50
): Promise<{ brocode: string; error?: string }> => {
  try {
    if (currentBroScore < rerollCost) {
      return { brocode: '', error: 'Insufficient BroScore for reroll' };
    }
    
    // Validate address
    const validation = validateAddress(walletAddress);
    if (!validation.isValid) {
      return { brocode: '', error: 'Invalid wallet address format' };
    }
    
    // Get current bro data
    const { data: currentBro } = await supabase
      .from('initiated_bros')
      .select('*')
      .eq('wallet_address', validation.normalizedAddress)
      .single();
      
    if (!currentBro) {
      return { brocode: '', error: 'User not found' };
    }
    
    // Preserve existing prefix and rank logic for rerolls
    const prefix = currentBro.brocode_prefix;
    const newBroCode = generateBroCodeWithPrefix(prefix);
    
    // Update with new BroCode and deduct cost
    const { data, error } = await supabase
      .from('initiated_bros')
      .update({
        brocode: newBroCode,
        bro_score: currentBroScore - rerollCost,
        xp_cost: (currentBro.xp_cost || 0) + rerollCost
      })
      .eq('wallet_address', validation.normalizedAddress)
      .select()
      .single();
      
    if (error) {
      console.error('❌ Error rerolling BroCode:', error);
      return { brocode: '', error: error.message };
    }
    
    // Log the reroll
    await logBroCodeGeneration(data.id, currentBro.brocode, newBroCode, 'reroll');
    
    console.log(`🎲 Successfully rerolled BroCode: ${currentBro.brocode} -> ${newBroCode}`);
    return { brocode: newBroCode };
    
  } catch (error) {
    console.error('❌ Unexpected error in rerollBroCode:', error);
    return { brocode: '', error: 'Failed to reroll BroCode' };
  }
};

// ✅ PRESERVED: Still needed for upgrade functionality
export const upgradeToBrometheus = async (
  walletAddress: string,
  currentBroScore: number,
  upgradeCost: number = 500
): Promise<{ brocode: string; error?: string }> => {
  try {
    if (currentBroScore < upgradeCost) {
      return { brocode: '', error: 'Insufficient BroScore for BROMETHEUS upgrade' };
    }
    
    // Validate address
    const validation = validateAddress(walletAddress);
    if (!validation.isValid) {
      return { brocode: '', error: 'Invalid wallet address format' };
    }
    
    // Get current bro data
    const { data: currentBro } = await supabase
      .from('initiated_bros')
      .select('*')
      .eq('wallet_address', validation.normalizedAddress)
      .single();
      
    if (!currentBro) {
      return { brocode: '', error: 'User not found' };
    }
    
    if (currentBro.is_brometheus) {
      return { brocode: currentBro.brocode, error: 'Already BROMETHEUS' };
    }
    
    // Generate BROMETHEUS BroCode
    const brometheusCode = generateBroCodeWithPrefix('BROMETHEUS');
    
    // Update to BROMETHEUS status
    const { data, error } = await supabase
      .from('initiated_bros')
      .update({
        brocode: brometheusCode,
        brocode_prefix: 'BROMETHEUS',
        rank: 'BROMETHEUS',
        is_brometheus: true,
        bro_score: currentBroScore - upgradeCost,
        xp_cost: (currentBro.xp_cost || 0) + upgradeCost
      })
      .eq('wallet_address', validation.normalizedAddress)
      .select()
      .single();
      
    if (error) {
      console.error('❌ Error upgrading to BROMETHEUS:', error);
      return { brocode: '', error: error.message };
    }
    
    // Log the upgrade
    await logBroCodeGeneration(data.id, currentBro.brocode, brometheusCode, 'brometheus_upgrade');
    
    console.log(`👑 Successfully upgraded to BROMETHEUS: ${currentBro.brocode} -> ${brometheusCode}`);
    return { brocode: brometheusCode };
    
  } catch (error) {
    console.error('❌ Unexpected error in upgradeToBrometheus:', error);
    return { brocode: '', error: 'Failed to upgrade to BROMETHEUS' };
  }
};
